import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getResponsiveCloudinaryUrls, BACKGROUND_IMAGES } from '../utils/cloudinary';
import { Users, Target, BookOpen, Heart } from 'lucide-react';
import Footer from '../components/layout/Footer';

const About = () => {
  const [backgroundImages, setBackgroundImages] = useState({
    about1: '',
    about2: '',
    about3: '',
    about4: ''
  });

  // Update background images based on screen size
  useEffect(() => {
    const updateBackgrounds = () => {
      const width = window.innerWidth;
      let size = 'desktop';

      if (width <= 768) {
        size = 'mobile';
      } else if (width <= 1280) {
        size = 'tablet';
      }

      const about1Urls = getResponsiveCloudinaryUrls(BACKGROUND_IMAGES.about1);
      const about2Urls = getResponsiveCloudinaryUrls(BACKGROUND_IMAGES.about2);
      const about3Urls = getResponsiveCloudinaryUrls(BACKGROUND_IMAGES.about3);
      const about4Urls = getResponsiveCloudinaryUrls(BACKGROUND_IMAGES.about4);

      setBackgroundImages({
        about1: about1Urls[size],
        about2: about2Urls[size],
        about3: about3Urls[size],
        about4: about4Urls[size]
      });
    };

    updateBackgrounds();
    window.addEventListener('resize', updateBackgrounds);
    return () => window.removeEventListener('resize', updateBackgrounds);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Navigation Bar */}
      <nav className="sticky top-0 left-0 right-0 z-40 bg-white/95 dark:bg-dark-bg-primary/95 backdrop-blur-md shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3">
              <div className="h-12 w-12 bg-white rounded-full flex items-center justify-center p-2 shadow-md border border-yellow-500">
                <img
                  src="/TheraPTrackLogoBgRemoved.png"
                  alt="TheraP Track Logo"
                  className="h-full w-full object-contain"
                />
              </div>
              <span className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">
                TheraP Track
              </span>
            </Link>

            <Link
              to="/login"
              className="px-6 py-2 bg-primary-600 dark:bg-dark-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 dark:hover:bg-dark-primary-700 transition"
            >
              Login
            </Link>
          </div>
        </div>
      </nav>

      {/* About Section */}
      <section className="py-24 relative">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40 dark:opacity-30"
          style={{
            backgroundImage: backgroundImages.about1
              ? `url('${backgroundImages.about1}')`
              : 'none'
          }}
        ></div>
        {/* Light overlay to ensure readability */}
        <div className="absolute inset-0 bg-white/10 dark:bg-dark-bg-primary/60"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
              About
            </h1>
            <div className="w-24 h-1 bg-primary-600 dark:bg-dark-primary-600 mx-auto mb-6"></div>
          </div>

          {/* About Content */}
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="bg-white/80 dark:bg-dark-bg-tertiary/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl">
              <p className="text-lg text-gray-700 dark:text-dark-text-secondary leading-relaxed mb-4">
                TheraP Track is a professional therapy and well-being platform designed to help individuals find the right therapist, explore therapeutic services, and access learning opportunities in a safe and structured environment.
              </p>
              <p className="text-lg text-gray-700 dark:text-dark-text-secondary leading-relaxed mb-4">
                We connect people with verified therapists and practitioners across a wide range of therapy and well-being disciplines, including counselling psychology, clinical therapy, hypnotherapy, breathing therapy, physiotherapy, and other integrative therapeutic approaches. Through TheraP Track, individuals can search for therapists, book therapy sessions, and explore both online and offline therapy options based on their needs.
              </p>
              <p className="text-lg text-gray-700 dark:text-dark-text-secondary leading-relaxed mb-4">
                Beyond therapy services, TheraP Track also serves as a growing learning and development space. We offer webinars, workshops, seminars, internships, and training programs led by experienced professionals, supporting students, early-career practitioners, and those interested in expanding their understanding of therapy and well-being practices.
              </p>
              <p className="text-lg text-gray-700 dark:text-dark-text-secondary leading-relaxed">
                Our platform is built with a strong focus on ethical practice, professional boundaries, privacy, and transparency. Every feature on TheraP Track is designed to support responsible therapy, meaningful learning, and informed decision-making—ensuring that clients, therapists, and learners feel confident, respected, and supported throughout their journey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why TheraP Track Exists Section */}
      <section className="py-24 relative bg-gray-50 dark:bg-dark-bg-secondary">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30 dark:opacity-20"
          style={{
            backgroundImage: backgroundImages.about2
              ? `url('${backgroundImages.about2}')`
              : 'none'
          }}
        ></div>
        {/* Light overlay to ensure readability */}
        <div className="absolute inset-0 bg-white/20 dark:bg-dark-bg-secondary/70"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <Target className="h-12 w-12 text-primary-600 dark:text-dark-primary-500" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
              Why TheraP Track Exists
            </h2>
            <div className="w-24 h-1 bg-primary-600 dark:bg-dark-primary-600 mx-auto"></div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white/80 dark:bg-dark-bg-tertiary/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl space-y-6">
              <p className="text-lg text-gray-700 dark:text-dark-text-secondary leading-relaxed">
                As therapy and well-being practices continue to expand, people often struggle to find the right kind of support in a space that feels professional, ethical, and reliable. At the same time, therapists and practitioners across different disciplines need structured platforms that respect boundaries, qualifications, and responsible practice.
              </p>
              <p className="text-lg font-semibold text-primary-700 dark:text-dark-primary-400">
                TheraP Track exists to bridge this gap.
              </p>
              <p className="text-lg text-gray-700 dark:text-dark-text-secondary leading-relaxed">
                We created TheraP Track to offer a clear, well-defined ecosystem where individuals can explore therapy and well-being services with confidence, and where therapists and practitioners can offer their work within a transparent and ethical framework.
              </p>
              <p className="text-lg text-gray-700 dark:text-dark-text-secondary leading-relaxed">
                By bringing together therapy services, learning opportunities, and professional development in one place, TheraP Track aims to reduce confusion, improve access, and support informed choices—whether someone is seeking therapy, offering services, or learning from experienced professionals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-24 relative">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40 dark:opacity-30"
          style={{
            backgroundImage: backgroundImages.about3
              ? `url('${backgroundImages.about3}')`
              : 'none'
          }}
        ></div>
        {/* Light overlay to ensure readability */}
        <div className="absolute inset-0 bg-white/10 dark:bg-dark-bg-primary/60"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <Heart className="h-12 w-12 text-primary-600 dark:text-dark-primary-500" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
              Our Approach
            </h2>
            <div className="w-24 h-1 bg-primary-600 dark:bg-dark-primary-600 mx-auto"></div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white/80 dark:bg-dark-bg-tertiary/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl space-y-6">
              <p className="text-lg text-gray-700 dark:text-dark-text-secondary leading-relaxed">
                At TheraP Track, we believe therapy and well-being services should be offered within a framework that values professionalism, responsibility, and respect for individual needs.
              </p>
              <div className="space-y-3">
                <p className="text-lg font-semibold text-gray-800 dark:text-dark-text-primary">
                  Our approach is grounded in:
                </p>
                <ul className="space-y-3 text-lg text-gray-700 dark:text-dark-text-secondary">
                  <li className="flex items-start gap-3">
                    <span className="text-primary-600 dark:text-dark-primary-500 font-bold text-xl">•</span>
                    <span>Ethical and transparent practice across therapy disciplines</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary-600 dark:text-dark-primary-500 font-bold text-xl">•</span>
                    <span>Clear communication and well-defined boundaries</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary-600 dark:text-dark-primary-500 font-bold text-xl">•</span>
                    <span>Respect for diverse therapeutic methods and learning pathways</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary-600 dark:text-dark-primary-500 font-bold text-xl">•</span>
                    <span>A commitment to privacy, safety, and informed participation</span>
                  </li>
                </ul>
              </div>
              <p className="text-lg text-gray-700 dark:text-dark-text-secondary leading-relaxed">
                We recognize that therapy is not one-size-fits-all. By supporting multiple therapeutic approaches and learning formats, TheraP Track allows individuals to explore care and growth options that align with their personal goals, while ensuring practitioners operate within structured and accountable systems.
              </p>
              <p className="text-lg text-gray-700 dark:text-dark-text-secondary leading-relaxed">
                This balanced approach helps create an environment where clients feel secure, therapists feel supported, and learners feel guided.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who TheraP Track is for Section */}
      <section className="py-24 relative bg-gray-50 dark:bg-dark-bg-secondary">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30 dark:opacity-20"
          style={{
            backgroundImage: backgroundImages.about4
              ? `url('${backgroundImages.about4}')`
              : 'none'
          }}
        ></div>
        {/* Light overlay to ensure readability */}
        <div className="absolute inset-0 bg-white/20 dark:bg-dark-bg-secondary/70"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <Users className="h-12 w-12 text-primary-600 dark:text-dark-primary-500" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-dark-text-primary mb-4">
              Who TheraP Track is for
            </h2>
            <div className="w-24 h-1 bg-primary-600 dark:bg-dark-primary-600 mx-auto"></div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white/80 dark:bg-dark-bg-tertiary/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl space-y-8">
              <p className="text-lg text-gray-700 dark:text-dark-text-secondary leading-relaxed">
                TheraP Track is designed to support individuals at different stages of their therapy and well-being journey. Our platform brings together those seeking support, those offering therapeutic services, and those learning to grow within the field.
              </p>

              {/* For Individuals */}
              <div className="space-y-3">
                <h3 className="text-xl font-bold text-primary-700 dark:text-dark-primary-400">
                  For Individuals
                </h3>
                <p className="text-lg text-gray-700 dark:text-dark-text-secondary leading-relaxed">
                  TheraP Track supports individuals looking for therapy and well-being services that align with their personal needs. Whether someone is seeking one-on-one therapy, couple or family support, or exploring alternative therapeutic approaches, the platform helps them find qualified professionals in a structured and transparent way.
                </p>
              </div>

              {/* For Therapists & Practitioners */}
              <div className="space-y-3">
                <h3 className="text-xl font-bold text-primary-700 dark:text-dark-primary-400">
                  For Therapists & Practitioners
                </h3>
                <p className="text-lg text-gray-700 dark:text-dark-text-secondary leading-relaxed">
                  We provide therapists and practitioners across disciplines with a professional space to offer their services responsibly. TheraP Track supports ethical practice, clear boundaries, and meaningful connections—allowing practitioners to focus on their work while operating within a trusted framework.
                </p>
              </div>

              {/* For Students & Learners */}
              <div className="space-y-3">
                <h3 className="text-xl font-bold text-primary-700 dark:text-dark-primary-400">
                  For Students & Learners
                </h3>
                <p className="text-lg text-gray-700 dark:text-dark-text-secondary leading-relaxed">
                  TheraP Track also serves as a learning space for students and early-career professionals. Through webinars, workshops, seminars, internships, and training opportunities, learners can gain exposure, guidance, and knowledge from experienced professionals across therapy and well-being fields.
                </p>
              </div>
            </div>
          </div>

          {/* Back to Home Button */}
          <div className="text-center pt-12">
            <Link
              to="/"
              className="inline-block px-8 py-3 bg-primary-600 dark:bg-dark-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 dark:hover:bg-dark-primary-700 transition"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default About;
